import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";

/**
 * Subscribe to Postgres changes on a table and invalidate that resource's React
 * Query cache, so the UI reflects external writes — e.g. the Plane -> CRM webhook
 * flipping a task's `done_date` — without a manual refresh.
 *
 * Realtime enforces RLS, so a client only receives changes for rows it may read.
 * The table must be in the `supabase_realtime` publication (see the realtime
 * migration), otherwise nothing is broadcast.
 */
// Bumped on every subscribe, so no two subscriptions ever share a topic.
let channelSeq = 0;

export function useRealtimeResource(resource: string): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    // A fresh topic per effect run. realtime-js hands back the existing channel
    // when a topic is already registered, and removeChannel only unregisters it
    // once the server acknowledges the leave. A reused topic therefore gets the
    // old, leaving channel back, and subscribe() on it is a no-op: no updates,
    // no error. That happens when one subscriber replaces another in a single
    // commit (Action Items to a Contact page), and on every mount in dev, where
    // StrictMode runs the effect, cleans it up and runs it again at once. A
    // per-mount useId is not enough for either: StrictMode keeps the id.
    // postgres_changes filters by schema and table, not by topic, so a unique
    // name changes nothing else. LCATOMICZO-31.
    channelSeq += 1;
    const channel = supabase
      .channel(`${resource}:${channelSeq}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: resource },
        () => {
          // Any change -> refetch this resource's lists and detail queries.
          queryClient.invalidateQueries({ queryKey: [resource] });
        },
      )
      .subscribe((status) => {
        // Silent on success; surface only a failed/dropped connection.
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          console.warn(`[realtime] ${resource} channel ${status}`);
        }
      });

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [resource, queryClient]);
}
